import React, { useState, useEffect } from 'react';
import { getSchoolOwners,updateApprovalStatus } from '../services/api';
import { FaEye } from 'react-icons/fa';
import ReactPaginate from 'react-paginate';
import { useLocation } from 'react-router-dom';

const SchoolOwnersDocuments = () => {
  const [owners, setOwners] = useState([]);
  const [allOwners, setAllOwners] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState({
    approvalStatus: '',
    isDualRole: '',
    isOnboardingComplete: '',
  });
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [expandedRows, setExpandedRows] = useState({});
  const [search, setSearch] = useState('');
  const [editModeUid, setEditModeUid] = useState(null);
  const [editedStatus, setEditedStatus] = useState('');
  const [selectedUids, setSelectedUids] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const data = await getSchoolOwners();
        setAllOwners(Array.isArray(data) ? data : data.documents || []);
      } catch (error) {
        console.error('Failed to fetch school owners:', error);
      }
    };

    fetchOwners();
  }, []);

  useEffect(() => {
    const applyFilters = () => {
      let filtered = [...allOwners];

      // Filter by approval status, dual role, onboarding
      if (filters.approvalStatus)
        filtered = filtered.filter((o) => o.approvalStatus === filters.approvalStatus);

      if (filters.isDualRole)
        filtered = filtered.filter((o) => String(o.isDualRole) === filters.isDualRole);

      if (filters.isOnboardingComplete)
        filtered = filtered.filter((o) => String(o.isOnboardingComplete) === filters.isOnboardingComplete);

      // Search by phone, name, or school name
      if (search.trim()) {
        const lowerSearch = search.toLowerCase();
        filtered = filtered.filter((o) =>
          o.ownerName?.toLowerCase().includes(lowerSearch) ||
          o.schoolName?.toLowerCase().includes(lowerSearch) ||
          o.phoneNumber?.includes(lowerSearch) ||
          o.pincode?.includes(lowerSearch) ||
          o.uid?.toLowerCase().includes(lowerSearch)
        );
      }

      // Sort
      filtered.sort((a, b) => {
        const dateA = a[sort]?.seconds || 0;
        const dateB = b[sort]?.seconds || 0;
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      });

      const start = currentPage * 10;
      const paginated = filtered.slice(start, start + 10);

      setOwners(paginated);
      setPageCount(Math.ceil(filtered.length / 10));
    };

    applyFilters();
  }, [allOwners, filters, sort, order, currentPage, search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idFromUrl = params.get('schoolOwnerId');
    if (idFromUrl) {
      setSearch(idFromUrl);
    }
  }, [location.search]);
useEffect(() => {
    const params = new URLSearchParams(location.search);
    const numberFromUrl = params.get('phoneNumber');
    if (numberFromUrl) {
      setSearch(numberFromUrl);
    }
  }, [location.search]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setCurrentPage(0);
  };

  const handleSortChange = (field) => {
    setSort(field);
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setCurrentPage(0);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getDocumentLinks = (documents) => {
    const availableDocs = [];
    if (documents?.bankAccountProofPaths?.length)
      availableDocs.push({ name: 'Bank Proof', path: documents.bankAccountProofPaths[0] });
    if (documents?.drivingSchoolPermitPaths?.length)
      availableDocs.push({ name: 'Permit', path: documents.drivingSchoolPermitPaths[0] });
    if (documents?.ownersAadhaarCardPaths?.length)
      availableDocs.push({ name: 'Aadhaar', path: documents.ownersAadhaarCardPaths[0] });
    if (documents?.ownersPANCardPaths?.length)
      availableDocs.push({ name: 'PAN', path: documents.ownersPANCardPaths[0] });
    return availableDocs;
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-poppins text-driveeasy-blue dark:text-driveeasy-blue2 mb-4">School Owners Documents</h2>

      {/* Search and Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Search by name, school, phone..."
          className="p-2 border rounded-md w-72 bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select name="approvalStatus" value={filters.approvalStatus} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <select name="isDualRole" value={filters.isDualRole} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">All Roles</option>
          <option value="true">Dual Role</option>
          <option value="false">Non-Dual Role</option>
        </select>
        <select name="isOnboardingComplete" value={filters.isOnboardingComplete} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">All Onboarding</option>
          <option value="true">Complete</option>
          <option value="false">Incomplete</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-neutral-800 shadow rounded-lg text-sm border border-gray-100 dark:border-neutral-800">
          <thead>
            <tr className="bg-driveeasy-blue text-white dark:bg-neutral-700 dark:text-gray-200">

              <th className="p-3 text-left">Doc ID</th>
              <th className="p-3 text-left">School Name</th>
              <th className="p-3 text-left">Owner Name</th>
              <th className="p-3 text-left">Phone Number</th>
              <th className="p-3 text-left">Approval Status</th>
              <th className="p-3 text-left">Documents</th>
              <th
                className="p-3 text-left cursor-pointer"
                onClick={() => handleSortChange('createdAt')}
              >
                Joined Date {sort === 'createdAt' && (order === 'asc' ? '↑' : '↓')}
              </th>
              <th className="p-3 text-left">Actions</th>
              <th className="p-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedUids.length === owners.length && owners.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUids(owners.map((o) => o.uid));
                      } else {
                        setSelectedUids([]);
                      }
                    }}
                  />
                </th>
            </tr>
          </thead>
          <tbody>
            {owners.length > 0 ? owners.map((owner) => (
              <React.Fragment key={owner.uid}>
                <tr className="border-b border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700/70 text-black dark:text-gray-300">
                  <td
                    className="p-3 text-driveeasy-blue hover:underline cursor-pointer"
                    onClick={() => {
                      setSelectedOwner(owner);
                      setProfileModalOpen(true);
                    }}
                  >
                    {owner.uid}
                  </td>
                  <td className="p-3">{owner.schoolName || 'N/A'}</td>
                  <td className="p-3">{owner.ownerName || 'N/A'}</td>
                  <td className="p-3">{owner.phoneNumber || 'N/A'}</td>
                 <td className="p-3">
                   {editModeUid === owner.uid ? (
                     <select
                       value={editedStatus}
                       onChange={(e) => setEditedStatus(e.target.value)}
                       className="border p-1 rounded"
                     >
                       {['PENDING', 'APPROVED', 'REJECTED'].map((status) => (
                         <option key={status} value={status}>{status}</option>
                       ))}
                     </select>
                   ) : (
                     <span className={`px-2 py-1 rounded ${owner.approvalStatus === 'PENDING'
                       ? 'bg-orange-500' : owner.approvalStatus === 'APPROVED'
                         ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                       {owner.approvalStatus || 'N/A'}
                     </span>
                   )}
                 </td>
                  <td className="p-3">
                    {getDocumentLinks(owner.documents).map((doc) => (
                      <div key={doc.name}>
                        <a href={doc.path} target="_blank" rel="noopener noreferrer" className="text-driveeasy-blue hover:underline">
                          {doc.name}
                        </a>
                      </div>
                    ))}
                  </td>
                  <td className="p-3">
                    {owner.createdAt?.seconds ? new Date(owner.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-3">
                    <button onClick={() => toggleRow(owner.uid)}>
                      <FaEye className={expandedRows[owner.uid] ? 'text-driveeasy-yellow' : 'text-driveeasy-blue'} />
                    </button>
                  </td>

                  <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedUids.includes(owner.uid)}
                        onChange={() =>
                          setSelectedUids((prev) =>
                            prev.includes(owner.uid)
                              ? prev.filter((id) => id !== owner.uid)
                              : [...prev, owner.uid]
                          )
                        }
                      />
                    </td>
                </tr>
                {expandedRows[owner.uid] && (
                  <tr>
                    <td colSpan="8" className="p-3 bg-gray-50 text-sm dark:bg-neutral-800">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-poppins text-driveeasy-blue">Address Details</h4>
                          <p>Address: {owner.addressLine1 || 'N/A'}</p>
                          <p>City: {owner.city || 'N/A'}</p>
                          {owner.landmark && <p>Landmark: {owner.landmark}</p>}
                          <p>State: {owner.state || 'N/A'}</p>
                          <p>Pincode: {owner.pincode || 'N/A'}</p>
                          <p>Location: {owner.location?.latitude || 'N/A'}, {owner.location?.longitude || 'N/A'}</p>
                          <p>Email: {owner.email || 'N/A'}</p>
                        </div>
                        <div>
                          <h4 className="font-poppins text-driveeasy-blue">Document Details</h4>
                          <p>Aadhaar Number: {owner.aadhaarNumber || 'N/A'}</p>
                          <p>PAN Number: {owner.panNumber || 'N/A'}</p>
                          <p>Dual Role: {owner.isDualRole ? '✅' : '❌'}</p>
                          <p>Onboarding Complete: {owner.isOnboardingComplete ? '✅' : '❌'}</p>
                          <h4 className="font-poppins text-driveeasy-blue mt-2">Sub-Collections</h4>
                          <p>Cars: {owner.cars?.map((car) => car.id).join(', ') || 'None'}</p>
                          <p>Instructors: {owner.instructors?.map((inst) => inst.id).join(', ') || 'None'}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )) : (
              <tr>
                <td colSpan="8" className="text-center p-4">No records found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <ReactPaginate
        previousLabel={'Previous'}
        nextLabel={'Next'}
        pageCount={pageCount}
        onPageChange={handlePageChange}
        containerClassName={'flex justify-center mt-4 space-x-2'}
        pageClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-600 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
                activeClassName={'bg-driveeasy-blue text-white'}
                previousClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-700 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
                nextClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-700 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
              />
      {selectedUids.length > 0 && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 shadow-lg px-6 py-3 rounded-lg flex items-center gap-4 z-50 text-gray-700 dark:text-gray-400">
          <span className="text-gray-700 dark:text-white">{selectedUids.length} selected</span>
          <button
            onClick={() => {
              setBulkAction('approve');
              setShowConfirmModal(true);
            }}
            className="bg-green-500 text-white px-3 py-1 rounded"
          >
            Approve Selected
          </button>
          <button
            onClick={() => {
              setBulkAction('reject');
              setShowConfirmModal(true);
            }}
            className="bg-red-500 text-white px-3 py-1 rounded"
          >
            Reject Selected
          </button>
          <button
            onClick={() => setSelectedUids([])}
            className="text-gray-600 underline dark:text-white underline"
          >
            Clear
          </button>
        </div>
      )}
      {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-neutral-800 dark:text-gray-200 p-6 rounded shadow-lg w-96 border border-gray-200 dark:border-neutral-700">
              <h2 className="text-lg font-bold mb-4">Confirm Action</h2>
              <p className="mb-4">
                {bulkAction === 'single'
                  ? `Are you sure you want to update status to "${editedStatus}"?`
                  : `Are you sure you want to ${bulkAction} ${selectedUids.length} selected school owners?`}
              </p>
              <div className="flex justify-end gap-4">
                <button onClick={() => setShowConfirmModal(false)} className="text-gray-600">Cancel</button>
                <button
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      if (bulkAction === 'single') {
                        await updateApprovalStatus(editModeUid, editedStatus);
                        setAllOwners((prev) =>
                          prev.map((o) =>
                            o.uid === editModeUid ? { ...o, approvalStatus: editedStatus } : o
                          )
                        );
                        setEditModeUid(null);
                      } else {
                        const updated = await Promise.all(
                          selectedUids.map((uid) =>
                            updateApprovalStatus(uid, bulkAction === 'approve' ? 'APPROVED' : 'REJECTED')
                          )
                        );
                        setAllOwners((prev) =>
                          prev.map((o) =>
                            selectedUids.includes(o.uid)
                              ? { ...o, approvalStatus: bulkAction === 'approve' ? 'APPROVED' : 'REJECTED' }
                              : o
                          )
                        );
                        setSelectedUids([]);
                      }
                      setShowConfirmModal(false);
                    } catch (error) {
                      console.error("Update failed", error);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  className="bg-driveeasy-blue text-white px-4 py-2 rounded"
                  disabled={isLoading}
                >
                  {isLoading ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
        {profileModalOpen && selectedOwner && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white dark:bg-neutral-800 dark:text-gray-200 p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-neutral-700">
              <h3 className="text-xl font-bold mb-4 text-driveeasy-blue">Full Profile</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-semibold text-driveeasy-blue">Owner Info</h4>
                  <p><strong>UID:</strong> {selectedOwner.uid}</p>
                  <p><strong>Owner Name:</strong> {selectedOwner.ownerName || 'N/A'}</p>
                  <p><strong>Phone:</strong> {selectedOwner.phoneNumber || 'N/A'}</p>
                  <p><strong>Email:</strong> {selectedOwner.email || 'N/A'}</p>
                  <p><strong>Aadhaar:</strong> {selectedOwner.aadhaarNumber || 'N/A'}</p>
                  <p><strong>PAN:</strong> {selectedOwner.panNumber || 'N/A'}</p>
                  <p><strong>Approval Status:</strong> {selectedOwner.approvalStatus}</p>
                  <p><strong>Dual Role:</strong> {selectedOwner.isDualRole ? 'Yes' : 'No'}</p>
                  <p><strong>Onboarding Complete:</strong> {selectedOwner.isOnboardingComplete ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-driveeasy-blue">Address</h4>
                  <p><strong>School Name:</strong> {selectedOwner.schoolName || 'N/A'}</p>
                  <p><strong>Address:</strong> {selectedOwner.addressLine1 || 'N/A'}</p>
                  <p><strong>City:</strong> {selectedOwner.city || 'N/A'}</p>
                  <p><strong>State:</strong> {selectedOwner.state || 'N/A'}</p>
                  <p><strong>Pincode:</strong> {selectedOwner.pincode || 'N/A'}</p>
                  <p><strong>Landmark:</strong> {selectedOwner.landmark || 'N/A'}</p>
                  <p><strong>Location:</strong> {selectedOwner.location?.latitude || 'N/A'}, {selectedOwner.location?.longitude || 'N/A'}</p>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  onClick={() => setProfileModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default SchoolOwnersDocuments;
